import {SubAPI} from "./SubAPI";

export class ProjectChangeset extends SubAPI {

    list(input: {
        project: number;
        status?: "pending"
    }) {
        return this.fetcher.get<ChangesetItem[]>("/api/project-changeset/list", input);
    }

    listForEntities(input: {
        project: number;
        openings?: number[];
        hardwareGroups?: number[];
    }) {
        return this.fetcher.post<{
            touched: ChangesetItem[];
            others: ChangesetItem[];
        }>("/api/project-changeset/list", input);
    }

    get(input: {
        id: number
    } | {
        activeForProject: number;
    }) {
        return this.fetcher.get<ChangesetItem>("/api/project-changeset", input);
    }

    start(input: {
        project: number;
    }) {
        return this.fetcher.post<ChangesetItem>("/api/project-changeset/start", input);
    }

    takeOver(input: {
        project: number;
    }) {
        return this.fetcher.post("/api/project-changeset/take-over", input)
    }

    upsert(input: Partial<ChangesetItem> & { project: number; closeSession?: boolean }) {
        return this.fetcher.post<ChangesetItem>("/api/project-changeset", input)
    }

    // set confirmClearPriceForInternal to skip ClearPriceForInternalError
    upsertTask(input: Partial<ChangesetItem> & {
        project: number;
        closeSession?: boolean;
        applySession?: boolean;
        denySession?: boolean;
        voiceClipIds?: number[];
        confirmClearPriceForInternal?: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/project-changeset/task", input)
    }

    close(input: {
        project: number;
        ignoreValidation?: boolean;
    }) {
        return this.fetcher.post("/api/project-changeset/close", input)
    }

    closeTask(input: {
        project: number;
    }) {
        return this.fetcher.post<{task: string}>("/api/project-changeset/close/task", input)
    }

    cancel(input: {
        project: number;
        force: boolean;
        ignoreValidation?: boolean;
        runSync: boolean;
    }) {
        return this.fetcher.post<{task: string}>("/api/project-changeset/cancel", input)
    }

    duplicate(input: {
        id: number;
    }) {
        return this.fetcher.post<{task: string}>("/api/project-changeset/duplicate", input)
    }

    reopen(input: {
        id: number;
    }) {
        return this.fetcher.post("/api/project-changeset/reopen", input)
    }

    reopenTask(input: {
        id: number;
    }) {
        return this.fetcher.post<{task: string}>("/api/project-changeset/reopen/task", input)
    }

    meta(input: {}) {
        return this.fetcher.get<{
            reasons: ChangeReason[];
            requestMethods: ChangeRequestMethod[];
        }>("/api/project-changeset/meta", input)
    }

    listChangeReasons() {
        return this.fetcher.get<ChangeReason[]>("/api/project-changeset-reason/list", {});
    }

    upsertChangeReason(input: Partial<ChangeReason>) {
        return this.fetcher.post<number>("/api/project-changeset-reason/upsert", input);
    }

    reSortChangeReasons(input: { ids: number[] }) {
        return this.fetcher.post<void>("/api/project-changeset-reason/re-sort", input);
    }

    listRequestMethods() {
        return this.fetcher.get<ChangeRequestMethod[]>("/api/project-changeset-request-method/list", {});
    }

    upsertRequestMethod(input: Partial<ChangeRequestMethod>) {
        return this.fetcher.post<number>("/api/project-changeset-request-method/upsert", input);
    }

    reSortRequestMethods(input: { ids: number[] }) {
        return this.fetcher.post<void>("/api/project-changeset-request-method/re-sort", input);
    }

    recalculateProducts(props: {
        project: number;
    }) {
        return this.fetcher.post("/api/change-released/recalculate-products", props)
    }

    updateCustomerSummary(props: {
        project: number;
        sections: Section[];
        notes: string
        title: string;
        changeset?: number;
    }) {
        return this.fetcher.post("/api/project-changeset/customer-summary", props)
    }

    getCustomerSummary(props: {
        project: number;
        changeset?: number;
    }) {
        return this.fetcher.get<{
            sections: Section[]
            notes: string;
            title: string;
            editedAfterApprovalAt: string | null;
            editedAfterApprovalByName: string | null;
        }>("/api/project-changeset/customer-summary", props)
    }

    getChangeWarnings(props: {
        changeset: number;
    }) {
        return this.fetcher.get<ChangeWarnings>("/api/project-changeset/change-warnings", props)
    }
}

export type ChangeWarnings = {
    conflicts: ConflictItem[];
    unresolved: UnresolvedItem[];
}

export type UnresolvedItem = {
    name: string;
    isForExisting: boolean;
}

export type ConflictItem = {
    name: string;
    conflictingChangeset: number | null;
    conflictingProjSeqNumber: number | null;
    conflictingChangesetMergedIntoMain: string | null;
}

export type Section = {
    id: number;
    name: string;
    description: string;
    amountCents: number;
}

export type ChangeReason = {
    id: number;
    name: string;
    archived: boolean;
    sortOrder: number;
}

export type ChangeRequestMethod = {
    id: number;
    name: string;
    archived: boolean;
    sortOrder: number;
}

// see ClearPriceForInternalError in core/project/changeset/main.go
export const ClearPriceForInternalError = "Making this change internal-only will clear the customer summary price. Please confirm to continue.";

export enum ChangesetStatus {
    Unknown = "unknown",
    PendingReview = "pending-review",
    Reviewed = "reviewed",
    ChangesCreated = "changes-created",
    SentToClient = "sent-to-client",
    ClientApproved = "client-approved",
    InternalApproved = "internal-approved",
    ClientDenied = "client-denied",
    Ignored = "ignored",
    Conflict = "conflict",
    Warning = "warning",
}

export type ChangesetItem = {
    id: number;
    project: number;
    name: string;
    description: string;
    createdAt: string;
    createdByName: string;
    createdBy: number;
    lockedByName: string | null;
    lockedBy: number | null;
    isActive: boolean;
    approved: boolean | null;
    approvalFile: number | null;
    archived: boolean;
    projSeqNumber: number;
    requestedByContact: number | null;
    requestedByContactName: string | null;
    status: ChangesetStatus;
    calcStatus?: ChangesetStatus;

    sendWarningCount?: number;
    existingWarningCount?: number;

    affectsOurScope: boolean | null;
    changeReason: number | null;
    changeReasonName: string | null;
    requestMethod: number | null;
    requestMethodName: string | null;
    clientChangeRef: string;
    sovReviewNotes: string | null;
    // sum of the customer change summary section amounts
    sovTotalCents: number | null;
    internalOnly: boolean;
    hasProductSpecRecalc: boolean;
    freeformChange: number | null;

    // approvedAt if set, otherwise updatedAt
    dateCalc: string;
    updatedAt: string;
}
